import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

const FAKE_LOTS_BY_FARM = {
  "farm-001": [
    { id: "lot-001A", name: "Lote A" },
    { id: "lot-001B", name: "Lote B" },
    { id: "lot-001C", name: "Lote C" },
    { id: "lot-001D", name: "Lote D" },
    { id: "lot-001E", name: "Lote E" },
  ],
  "farm-002": [
    { id: "lot-002A", name: "Lote A" },
    { id: "lot-002B", name: "Lote B" },
    { id: "lot-002C", name: "Lote C" },
    { id: "lot-002D", name: "Lote D" },
    { id: "lot-002E", name: "Lote E" },
  ],
};

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const TASK_TYPES = [
  "",
  "Guadañar",
  "Brechas",
  "Linderos",
  "Cosechar",
  "Podas de Cultivo",
  "Injertar",
  "Otro",
];

const FieldOperationForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    farmId: "",
    farmName: "",
    lotId: "",
    lotName: "",
    performedById: "",
    performedByName: "",
    otherParticipants: [], // array of collaborator ids
    taskType: "",
    timeSpentHours: "",
    additionalDescriptions: "",
    photoEvidence: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableLots, setAvailableLots] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });

    if (initialValues.farmId) {
      const lots = FAKE_LOTS_BY_FARM[initialValues.farmId] || [];
      setAvailableLots(lots);
      if (initialValues.lotId && !initialValues.lotName) {
        const foundLot = lots.find((l) => l.id === initialValues.lotId);
        if (foundLot) setForm((f) => ({ ...f, lotName: foundLot.name }));
      }
    }

    if (initialValues.performedById && !initialValues.performedByName) {
      const found = FAKE_COLLABORATORS.find(
        (c) => c.id === initialValues.performedById
      );
      if (found) setForm((f) => ({ ...f, performedByName: found.name }));
    }

    // if otherParticipants provided as array, keep it; if string CSV, convert to array
    if (typeof initialValues.otherParticipants === "string") {
      const arr = initialValues.otherParticipants
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      setForm((f) => ({ ...f, otherParticipants: arr }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFarmChange = (farmId) => {
    const farm = FAKE_FARMS.find((f) => f.id === farmId);
    const lots = FAKE_LOTS_BY_FARM[farmId] || [];
    setAvailableLots(lots);
    setForm({
      ...form,
      farmId,
      farmName: farm ? farm.name : "",
      lotId: "",
      lotName: "",
    });
  };

  const handleLotChange = (lotId) => {
    const lot = (availableLots || []).find((l) => l.id === lotId);
    setForm({ ...form, lotId, lotName: lot ? lot.name : "" });
  };

  const handleOtherParticipantsChange = (value) => {
    let arr = value;
    if (typeof value === "string") {
      arr = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    setForm({ ...form, otherParticipants: arr });
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    // if createdAt not set, add ISO date
    if (!payload.createdAt) payload.createdAt = new Date().toISOString();
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => handleFarmChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="lotId"
          label={strings.fields.lotId.label}
          value={form.lotId || ""}
          onChange={(e) => handleLotChange(e.target.value)}
        >
          <option value="">--</option>
          {availableLots.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_COLLABORATORS.find((c) => c.id === val);
            setForm({
              ...form,
              performedById: val,
              performedByName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <TextField
        name="otherParticipants"
        label={strings.fields.otherParticipants.label}
        placeholder="col-0001, col-0002"
        value={
          Array.isArray(form.otherParticipants)
            ? form.otherParticipants.join(", ")
            : form.otherParticipants || ""
        }
        onChange={(e) => handleOtherParticipantsChange(e.target.value)}
      />

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="taskType"
          label={strings.fields.taskType.label}
          value={form.taskType || ""}
          onChange={(e) => setForm({ ...form, taskType: e.target.value })}
        >
          {TASK_TYPES.map((t) => (
            <option key={t} value={t}>
              {t || "--"}
            </option>
          ))}
        </SelectField>

        <TextField
          name="timeSpentHours"
          label={strings.fields.timeSpentHours.label}
          placeholder="e.g. 1.5"
          value={form.timeSpentHours || ""}
          onChange={(e) => setForm({ ...form, timeSpentHours: e.target.value })}
        />
      </Grid>

      <TextAreaField
        name="additionalDescriptions"
        label={strings.fields.additionalDescriptions.label}
        value={form.additionalDescriptions || ""}
        onChange={(e) =>
          setForm({ ...form, additionalDescriptions: e.target.value })
        }
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha" },
      farmId: { label: "Finca" },
      lotId: { label: "Lote" },
      performedById: { label: "Persona que realiza" },
      otherParticipants: { label: "Otras personas" },
      taskType: { label: "Tarea" },
      timeSpentHours: { label: "Tiempo invertido (hrs)" },
      additionalDescriptions: { label: "Descripciones adicionales" },
      createdAt: { label: "Fecha de creación" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default FieldOperationForm;
